import round from 'lodash/round'
import type { TimelinePiece } from '../utils/mediaTimeline'
import { keptRangesWithin, rangesDuration, type Range } from './skipRanges'

/**
 * A scene and the transcript's cuts (#77): the one mapping every caller goes
 * through.
 *
 * A scene is a WINDOW of a recording, in recording time. What it plays is the
 * window minus the transcript's cuts (removed words and `skipSegments`, as
 * `skipRangesOf` derives them), back to back: its PIECES. Its length on the
 * timeline is their sum. Cuts are never stored in a project; they come from
 * the transcript at hydration, so a cut or restore made later reaches the
 * scene at the next sync (`sceneSync.ts`).
 *
 * ```
 *   window     [start ──────────────────────────────────── end]
 *   cuts             ▓▓▓▓                    ▓▓▓▓▓▓
 *   pieces     [────)    (──────────────────)      (──────)
 *   offset     0 ──── a ──────────────────── b ────────── length
 * ```
 *
 * With no cut in the window there is one piece, the window itself, and
 * everything behaves exactly as before cuts were followed.
 */

export type SceneWindow = Range

/** The kept parts of `window`, recording-relative, in play order. */
export function piecesOf(
  window: SceneWindow,
  skips: readonly Range[],
): Range[] {
  return keptRangesWithin(skips, window.start, window.end)
}

/** Total kept length of `pieces`, seconds, rounded to milliseconds. */
export function keptLength(pieces: readonly Range[]): number {
  return rangesDuration(pieces)
}

/**
 * The recording time an item `offset` seconds in plays. An offset on a seam
 * lands on the far side of the cut (the next piece's start); one past the end
 * stays on the last piece's end.
 */
export function sourceTimeAt(pieces: readonly Range[], offset: number): number {
  let cursor = 0
  for (let i = 0; i < pieces.length; i++) {
    const piece = pieces[i]!
    const length = piece.end - piece.start
    const last = i === pieces.length - 1
    if (offset < cursor + length || last)
      return round(piece.start + Math.min(length, offset - cursor), 3)
    cursor += length
  }
  return 0
}

/**
 * Where recording `time` sits on the item, seconds from its start; `null`
 * inside a cut or outside the window.
 */
export function offsetOf(
  pieces: readonly Range[],
  time: number,
): number | null {
  let cursor = 0
  for (const piece of pieces) {
    if (time >= piece.start && time <= piece.end)
      return round(cursor + (time - piece.start), 3)
    cursor += piece.end - piece.start
  }
  return null
}

/**
 * The window whose kept length is `length`, moving only `edge` and stepping
 * over cuts outside the window as it grows. `bounds` is what the recording
 * has (`start ≥ min`, `end ≤ max`): a window that cannot grow far enough stops
 * there, shorter than asked.
 */
export function windowForLength(
  edge: 'start' | 'end',
  window: SceneWindow,
  skips: readonly Range[],
  length: number,
  bounds: { min?: number; max?: number } = {},
): SceneWindow {
  const min = bounds.min ?? 0
  const max = bounds.max ?? Number.POSITIVE_INFINITY
  let remaining = Math.max(0, length)

  if (edge === 'end') {
    let cursor = window.start
    for (const skip of skips) {
      if (skip.end <= cursor) continue
      if (skip.start > cursor) {
        const available = skip.start - cursor
        if (remaining <= available) break
        remaining -= available
      }
      cursor = Math.max(cursor, skip.end)
    }
    return {
      start: window.start,
      end: round(Math.min(max, cursor + remaining), 3),
    }
  }

  let cursor = window.end
  for (let i = skips.length - 1; i >= 0; i--) {
    const skip = skips[i]!
    if (skip.start >= cursor) continue
    if (skip.end < cursor) {
      const available = cursor - skip.end
      if (remaining <= available) break
      remaining -= available
    }
    cursor = Math.min(cursor, skip.start)
  }
  return {
    start: round(Math.max(min, cursor - remaining), 3),
    end: window.end,
  }
}

/**
 * The pieces as frames on the item's own timeline, one `<Sequence>` each.
 * Edges come from the running total, so the pieces tile the item with no
 * frame gained or lost to rounding, and they are clamped to
 * `durationInFrames`: the last piece ends exactly where the item does,
 * whatever the transcript says now, so playback can never overrun the length
 * a render billed. With nothing kept (every word cut), the window plays from
 * its start, as an item did before cuts were followed.
 */
export function framePieces({
  window,
  skips,
  durationInFrames,
  fps,
}: {
  window: SceneWindow
  skips: readonly Range[]
  durationInFrames: number
  fps: number
}): TimelinePiece[] {
  const kept = piecesOf(window, skips)
  const out: TimelinePiece[] = []
  let seconds = 0
  for (const piece of kept) {
    const from = Math.round(seconds * fps)
    seconds += piece.end - piece.start
    const to = Math.min(durationInFrames, Math.round(seconds * fps))
    if (from >= durationInFrames) break
    if (to <= from) continue
    const trimBefore = Math.round(piece.start * fps)
    out.push({
      from,
      durationInFrames: to - from,
      trimBefore,
      trimAfter: trimBefore + (to - from),
    })
  }
  const last = out[out.length - 1]
  if (!last) {
    const trimBefore = Math.round(window.start * fps)
    return [
      {
        from: 0,
        durationInFrames,
        trimBefore,
        trimAfter: trimBefore + durationInFrames,
      },
    ]
  }
  const end = last.from + last.durationInFrames
  if (end < durationInFrames) {
    last.durationInFrames = durationInFrames - last.from
    last.trimAfter = last.trimBefore + last.durationInFrames
  }
  return out
}
